import { toRelativeUrl } from "@okta/okta-auth-js";
import type { OktaAuth } from "@okta/okta-auth-js";
import { STORAGE_KEYS } from "../constants";

// Prevent multiple rapid redirects
let lastRedirectTime = 0;
const REDIRECT_DEBOUNCE_MS = 5000; // 5 seconds between redirects
let isRedirectInProgress = false;

// Loop detection - track redirects within a time window
let redirectTimestamps: number[] = [];
const LOOP_DETECTION_WINDOW_MS = 60000; // 1 minute window
const MAX_REDIRECTS_IN_WINDOW = 3; // Max 3 redirects per minute before stopping

/**
 * Clear local storage and redirect to Okta login
 * Reusable authentication redirect handler with debounce protection
 * 
 * @param oktaAuth - Okta authentication instance
 * @param reason - Optional reason for redirect (for logging)
 */
export const redirectToOktaLogin = async (
  oktaAuth: OktaAuth,
  reason?: string
): Promise<void> => {
  // If we're currently on Okta's login callback route, do NOT start another
  // redirect cycle. Doing so can cause an infinite Okta <-> /login/callback loop.
  if (globalThis.location?.pathname?.includes("/login/callback")) {
    console.warn(
      "Skipping redirectToOktaLogin while on /login/callback to avoid redirect loop"
    );
    return;
  }

  // Prevent multiple rapid redirects
  const now = Date.now();
  if (isRedirectInProgress || (now - lastRedirectTime) < REDIRECT_DEBOUNCE_MS) {
    console.warn(
      `Skipping redirectToOktaLogin - redirect already in progress or too soon (${now - lastRedirectTime}ms since last)`
    );
    return;
  }

  // Loop detection: check if we've redirected too many times recently
  // Clean up old timestamps outside the window
  redirectTimestamps = redirectTimestamps.filter(
    ts => now - ts < LOOP_DETECTION_WINDOW_MS
  );
  
  if (redirectTimestamps.length >= MAX_REDIRECTS_IN_WINDOW) {
    console.error(
      `REDIRECT LOOP DETECTED: ${redirectTimestamps.length} redirects in the last ${LOOP_DETECTION_WINDOW_MS / 1000}s. ` +
      `Stopping to prevent infinite loop. Please check authentication configuration.`
    );
    // Don't redirect - break the loop
    return;
  }
  
  // Record this redirect attempt
  redirectTimestamps.push(now);

  if (reason) {
    console.log(`Redirecting to Okta login: ${reason}`);
  }

  isRedirectInProgress = true;
  lastRedirectTime = now;

  // Do NOT clear all localStorage here. Okta Auth JS uses browser storage for
  // transaction state (PKCE, nonce/state) and wiping it can break callbacks.
  // Only clear app-owned keys - do NOT clear OKTA_TOKEN_STORAGE as Okta SDK manages it.
  // Clearing okta-token-storage causes validateOktaToken to fail after callback redirect.
  localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  // NOTE: Intentionally NOT removing OKTA_TOKEN_STORAGE - let Okta SDK manage it
  localStorage.removeItem("persist:root");

  try {
    const originalUri = toRelativeUrl(globalThis.location.href, globalThis.location.origin);
    oktaAuth.setOriginalUri(originalUri);
    await oktaAuth.signInWithRedirect();
  } finally {
    // Reset after a delay to allow for next redirect if needed
    setTimeout(() => {
      isRedirectInProgress = false;
    }, 1000);
  }
};

/**
 * Reset redirect state - primarily for testing
 */
export const resetRedirectState = (): void => {
  lastRedirectTime = 0;
  isRedirectInProgress = false;
  redirectTimestamps = [];
};


export const getTerminalId = () => {
  try {
    const persistRoot = localStorage.getItem("persist:root");
    if (!persistRoot) return "";

    const { activeTerminal } = JSON.parse(persistRoot);
    if (!activeTerminal) return "";

    const terminal =
      typeof activeTerminal === "string"
        ? JSON.parse(activeTerminal)
        : activeTerminal;

    return terminal?.terminalId ?? "";
  } catch {
    console.error("Failed to parse terminalId from localStorage");
    return "";
  }
};

